import React, { useEffect, useState } from 'react';
import { track } from '../lib/track';
import { authApi } from './api';
import { useAuth } from './AuthContext';
import { isPlatformSuperAdmin, PLATFORM_SUPER_ADMIN } from './roles';

const emptyForm = { email: '', name: '', role: 'user', send_invite: true };

export default function AdminUsers() {
  const { user } = useAuth();
  const canGrantPsa = isPlatformSuperAdmin(user);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setError('');
    try {
      setUsers(await authApi.listUsers());
    } catch (err) {
      setError(err.message || 'Failed to load users');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await authApi.createUser(form);
      track('admin_user_created', { role: form.role, send_invite: form.send_invite });
      setForm(emptyForm);
      setNotice('User created; invite email sent if SES succeeded.');
      await load();
    } catch (err) {
      setError(err.message || 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  const setRole = async (u, role) => {
    setError('');
    try {
      await authApi.updateUser(u.id, { role });
      track('admin_user_role_changed', { target_role: role });
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const setActive = async (u, active) => {
    setError('');
    try {
      await authApi.updateUser(u.id, { active });
      track('admin_user_active_toggled', { active });
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const resetPw = async (u) => {
    setError('');
    setNotice('');
    try {
      await authApi.adminReset(u.id);
      track('admin_password_reset_sent');
      setNotice(`Reset email sent to ${u.email}`);
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    setError('');
    try {
      await authApi.deleteUser(u.id);
      track('admin_user_deleted');
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-page">
      <div className="section-header">
        <h2>User admin</h2>
        <p>Create, update, disable, and email password resets. Admin tool is admin-role only.</p>
      </div>

      {error && <div className="auth-error">{error}</div>}
      {notice && <div className="auth-ok">{notice}</div>}

      <form className="admin-create" onSubmit={create}>
        <h3>Add user</h3>
        <div className="admin-create-grid">
          <label>
            Name
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </label>
          <label>
            Role
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              <option value="user">user</option>
              <option value="admin">admin (tenant)</option>
              {canGrantPsa && (
                <option value={PLATFORM_SUPER_ADMIN}>platform_super_admin</option>
              )}
            </select>
          </label>
          <label className="admin-check">
            <input
              type="checkbox"
              checked={form.send_invite}
              onChange={(e) => setForm({ ...form, send_invite: e.target.checked })}
            />
            Send SES invite
          </label>
        </div>
        <button type="submit" className="auth-primary" disabled={busy}>
          {busy ? 'Creating…' : 'Create user'}
        </button>
      </form>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>2FA</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.name}</td>
                <td>{u.email}</td>
                <td>
                  <select value={u.role} onChange={(e) => setRole(u, e.target.value)}>
                    <option value="user">user</option>
                    <option value="admin">admin (tenant)</option>
                    {(canGrantPsa || u.role === PLATFORM_SUPER_ADMIN) && (
                      <option value={PLATFORM_SUPER_ADMIN}>platform_super_admin</option>
                    )}
                    <option value="investor">investor</option>
                  </select>
                </td>
                <td>{u.totp_enabled ? 'on' : 'off'}</td>
                <td>{u.active ? 'yes' : 'no'}</td>
                <td className="admin-actions">
                  <button type="button" onClick={() => setActive(u, !u.active)}>
                    {u.active ? 'Disable' : 'Enable'}
                  </button>
                  <button type="button" onClick={() => resetPw(u)}>
                    Email reset
                  </button>
                  <button type="button" className="danger" onClick={() => remove(u)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
